import { useState } from 'react';

const faqs = [
  { q: 'How do I fund my wallet?', a: 'Go to Wallet page → Fund Wallet. Choose Bank Transfer or Crypto (BTC, ETH, USDT).' },
  { q: 'How long does crypto deposit take?', a: 'USDT (TRC-20) is near-instant. BTC and ETH take 5-30 mins.' },
  { q: 'Can I use my card on any website?', a: 'Yes! Your CardFlow Mastercard works on Amazon, Netflix, Shopify, AWS, and more.' },
  { q: 'How do I freeze my card?', a: 'Go to Cards page → click Freeze on any card. Unfreeze anytime.' },
  { q: 'What is the card creation fee?', a: 'Creating a virtual card is completely free.' },
];

const SupportWidget = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'home' | 'chat' | 'faq'>('home');
  const [messages, setMessages] = useState<{ from: 'user' | 'agent'; text: string }[]>([
    { from: 'agent', text: 'Hi! Welcome to CardFlow Support. How can I help you today?' },
  ]);
  const [input, setInput] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const sendMessage = () => {
    if (!input.trim()) return;
    const userMsg = input.trim();
    setMessages(prev => [...prev, { from: 'user', text: userMsg }]);
    setInput('');
    setTimeout(() => {
      const matched = faqs.find(f => f.q.toLowerCase().includes(userMsg.toLowerCase().split(' ')[0]));
      setMessages(prev => [...prev, {
        from: 'agent',
        text: matched ? matched.a : 'Thanks! Our team will respond shortly. Check FAQ for quick answers.',
      }]);
    }, 800);
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-emerald-500 hover:bg-emerald-400 rounded-full cursor-pointer transition-all duration-200 hover:scale-110"
        style={{ boxShadow: '0 0 24px rgba(16,185,129,0.4)' }}
      >
        <i className={`${open ? 'ri-close-line' : 'ri-customer-service-2-line'} text-white text-2xl`}></i>
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-80 bg-slate-900 border border-white/10 rounded-2xl overflow-hidden animate-slide-in-right" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
                <i className="ri-customer-service-2-line text-white text-xl"></i>
              </div>
              <div>
                <div className="text-white font-semibold text-sm">CardFlow Support</div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-emerald-300 rounded-full animate-pulse"></div>
                  <span className="text-emerald-100 text-xs">Online · Replies in minutes</span>
                </div>
              </div>
            </div>
          </div>

          {view === 'home' && (
            <div className="p-4 space-y-3">
              <p className="text-slate-300 text-sm">Hi there! How can we help you?</p>
              {[
                { icon: 'ri-chat-3-line', color: 'text-emerald-400', bg: 'bg-emerald-500/20', title: 'Chat with us', sub: 'Get help from our team', action: () => setView('chat') },
                { icon: 'ri-question-answer-line', color: 'text-amber-400', bg: 'bg-amber-500/20', title: 'FAQ', sub: 'Quick answers', action: () => setView('faq') },
              ].map((item) => (
                <button key={item.title} onClick={item.action} className="w-full flex items-center gap-3 p-3 bg-slate-800 hover:bg-slate-700 border border-white/5 rounded-xl transition-colors cursor-pointer text-left">
                  <div className={`w-9 h-9 flex items-center justify-center ${item.bg} rounded-lg flex-shrink-0`}>
                    <i className={`${item.icon} ${item.color}`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="text-white text-sm font-medium">{item.title}</div>
                    <div className="text-slate-500 text-xs">{item.sub}</div>
                  </div>
                  <i className="ri-arrow-right-s-line text-slate-500"></i>
                </button>
              ))}
              <a href="mailto:support@cardflow.io" className="w-full flex items-center gap-3 p-3 bg-slate-800 hover:bg-slate-700 border border-white/5 rounded-xl transition-colors cursor-pointer text-left">
                <div className="w-9 h-9 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-teal-400"></i>
                </div>
                <div className="flex-1">
                  <div className="text-white text-sm font-medium">Email Support</div>
                  <div className="text-slate-500 text-xs">support@cardflow.io</div>
                </div>
                <i className="ri-arrow-right-s-line text-slate-500"></i>
              </a>
            </div>
          )}

          {view === 'chat' && (
            <div className="flex flex-col h-80">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-white/5">
                <button onClick={() => setView('home')} className="text-slate-400 hover:text-white cursor-pointer"><i className="ri-arrow-left-s-line text-xl"></i></button>
                <span className="text-white text-sm font-medium">Live Chat</span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${msg.from === 'user' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-200'}`}>{msg.text}</div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-white/5 flex gap-2">
                <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} placeholder="Type a message..." className="flex-1 bg-slate-800 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-emerald-500" />
                <button onClick={sendMessage} className="w-9 h-9 flex items-center justify-center bg-emerald-500 hover:bg-emerald-400 rounded-xl cursor-pointer transition-colors flex-shrink-0">
                  <i className="ri-send-plane-line text-white text-sm"></i>
                </button>
              </div>
            </div>
          )}

          {view === 'faq' && (
            <div className="flex flex-col h-80">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-white/5">
                <button onClick={() => setView('home')} className="text-slate-400 hover:text-white cursor-pointer"><i className="ri-arrow-left-s-line text-xl"></i></button>
                <span className="text-white text-sm font-medium">FAQ</span>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {faqs.map((faq, i) => (
                  <div key={i} className="bg-slate-800 rounded-xl overflow-hidden">
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-3 text-left cursor-pointer">
                      <span className="text-white text-sm font-medium pr-2">{faq.q}</span>
                      <i className={`${openFaq === i ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-slate-400 flex-shrink-0`}></i>
                    </button>
                    {openFaq === i && <div className="px-3 pb-3 text-slate-400 text-xs leading-relaxed border-t border-white/5 pt-2">{faq.a}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SupportWidget;
